import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

const TYPE_ICON = { Article: "FileText", Guide: "BookOpen", Video: "PlayCircle", Template: "LayoutTemplate", Document: "File", Checklist: "ListChecks", Calculator: "Calculator", Tool: "Wrench", "External link": "Link" };
const CATEGORIES = ["Business strategy", "Leadership", "Finance", "Sales", "Marketing", "People and HR", "Operations", "Customer experience", "Compliance", "Technology", "Meeting management", "Templates and tools"];

export default function Resources() {
  const { orgId } = useOrg();
  const { isAmden, isSuper } = useAuth();
  const [resources, setResources] = useState(null);
  const [cat, setCat] = useState("all");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [assigning, setAssigning] = useState(null);

  const load = () => api.get(`/resources${orgId ? `?org_id=${orgId}` : ""}`).then((r) => setResources(r.data));
  useEffect(() => { setResources(null); load(); }, [orgId]);

  if (!resources) return <PageLoading />;

  const toggleSave = async (r) => {
    await api.post(`/resources/${r.id}/save`);
    load();
  };

  const deleteResource = async () => {
    try {
      await api.delete(`/resources/${deleteTarget.id}`);
      toast.success("Resource deleted");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const assigned = resources.filter((r) => r.assigned);
  const saved = resources.filter((r) => r.saved);
  const recent = [...resources].slice(0, 4);
  const featured = resources.filter((r) => r.featured);
  const shown = cat === "all" ? resources : resources.filter((r) => r.category === cat);
  const cats = ["all", ...CATEGORIES.filter((c) => resources.some((r) => r.category === c))];

  const Card = ({ r }) => {
    const Icon = Icons[TYPE_ICON[r.resource_type] || "FileText"] || Icons.FileText;
    return (
      <div className="amden-card amden-card-hover flex flex-col p-5" data-testid={`resource-${r.id}`}>
        <div className="flex items-start justify-between">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0B1729] text-[#FFC24D]"><Icon size={18} /></span>
          <div className="flex items-center gap-1.5">
            {isAmden && <button onClick={() => setAssigning(r)} className="text-[#1E2D44]/30 hover:text-[#FFB000]" title="Assign to clients" data-testid={`assign-resource-${r.id}`}><Icons.Share2 size={15} /></button>}
            {isSuper && <button onClick={() => setEditing(r)} className="text-[#1E2D44]/30 hover:text-[#FFB000]" title="Edit resource" data-testid={`edit-resource-${r.id}`}><Icons.Pencil size={15} /></button>}
            {isSuper && <button onClick={() => setDeleteTarget(r)} className="text-[#1E2D44]/30 hover:text-[#C95555]" title="Delete resource" data-testid={`delete-resource-${r.id}`}><Icons.Trash2 size={15} /></button>}
            <button onClick={() => toggleSave(r)} className="text-[#1E2D44]/30 hover:text-[#FFB000]" data-testid={`save-resource-${r.id}`}>
              <Icons.Bookmark size={17} fill={r.saved ? "#FFB000" : "none"} stroke={r.saved ? "#FFB000" : "currentColor"} />
            </button>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-[#FFB000]">{r.resource_type}</span>
          {r.assigned && <span className="rounded-full bg-[#3D8B65]/10 px-2 py-0.5 text-[10px] font-semibold text-[#3D8B65]">Assigned</span>}
        </div>
        <h3 className="mt-1 font-display text-base font-bold text-[#0B1729]">{r.title}</h3>
        <p className="mt-1 flex-1 text-sm text-[#1E2D44]/60 line-clamp-3">{r.description}</p>
        <div className="mt-4 flex items-center justify-between border-t border-[#EEF1F6] pt-3">
          <span className="text-xs text-[#1E2D44]/45">{r.category}</span>
          <a href={r.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-medium text-[#FFB000] hover:underline">Open <Icons.ArrowUpRight size={13} /></a>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="resources-page">
      <PageHeader eyebrow="Knowledge" title="Resource Library" subtitle="Guides, templates and tools recommended for your business." icon="BookOpen"
        actions={isAmden && <Button onClick={() => setCreating(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="new-resource-btn"><Icons.Plus size={16} className="mr-1.5" /> Add resource</Button>} />

      {assigned.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-[#0B1729]"><Icons.UserCheck size={18} className="text-[#3D8B65]" /> Assigned by your consultant</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{assigned.map((r) => <Card key={r.id} r={r} />)}</div>
        </section>
      )}

      {featured.length > 0 && cat === "all" && (
        <section className="mb-8">
          <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-[#0B1729]"><Icons.Star size={18} className="text-[#FFB000]" /> Recommended for you</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{featured.map((r) => <Card key={r.id} r={r} />)}</div>
        </section>
      )}

      <section>
        <div className="mb-4 flex flex-wrap gap-2">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${cat === c ? "bg-[#0B1729] text-white" : "bg-white text-[#1E2D44]/70 hover:bg-[#E4E8EF]"}`} data-testid={`res-cat-${c}`}>
              {c === "all" ? "All resources" : c}
            </button>
          ))}
        </div>
        {shown.length === 0 ? <EmptyState icon="BookOpen" title="No resources" description="Nothing in this category yet." /> : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{shown.map((r) => <Card key={r.id} r={r} />)}</div>
        )}
      </section>

      {creating && <ResourceDialog onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <ResourceDialog resource={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {assigning && <AssignDialog resource={assigning} onClose={() => { setAssigning(null); load(); }} />}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-resource-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this resource?</AlertDialogTitle>
            <AlertDialogDescription>"{deleteTarget?.title}" will be permanently removed from the library for everyone. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteResource} className="bg-[#C95555] hover:bg-[#b34848]" data-testid="confirm-delete-resource">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AssignDialog({ resource, onClose }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/resources/${resource.id}/assignments`).then((r) => { setData(r.data); setSelected(r.data.assigned); });
  }, [resource.id]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/resources/${resource.id}/assignments`, { organisation_ids: selected });
      toast.success("Client assignments updated");
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="assign-dialog">
        <DialogHeader><DialogTitle>Assign to clients</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Choose which clients this resource is assigned to.</DialogDescription>
        <p className="text-sm text-[#1E2D44]/60">Choose which clients see "{resource.title}" in their library.</p>
        {!data ? (
          <div className="py-8 text-center"><Icons.Loader2 className="mx-auto animate-spin text-[#FFB000]" size={22} /></div>
        ) : data.candidates.length === 0 ? (
          <p className="py-6 text-center text-sm text-[#1E2D44]/50">No clients available to assign.</p>
        ) : (
          <div className="max-h-72 space-y-1.5 overflow-y-auto">
            {data.candidates.map((o) => (
              <label key={o.id} className="flex cursor-pointer items-center gap-3 rounded-lg border border-[#E4E8EF] px-3 py-2.5 transition-colors hover:border-[#FFC24D]" data-testid={`assign-org-${o.id}`}>
                <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} className="h-4 w-4 accent-[#FFB000]" />
                <span className="text-sm font-medium text-[#0B1729]">{o.company_name}</span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !data} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-assign-btn">
            {saving ? <Icons.Loader2 className="animate-spin" size={16} /> : `Assign to ${selected.length} client(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResourceDialog({ resource, onClose, onSaved }) {  const isEdit = !!resource;
  const [form, setForm] = useState(resource ? {
    title: resource.title || "", description: resource.description || "", category: resource.category || CATEGORIES[0],
    resource_type: resource.resource_type || "Guide", url: resource.url || "", featured: !!resource.featured, published: resource.published !== false,
  } : { title: "", description: "", category: CATEGORIES[0], resource_type: "Guide", url: "", featured: false, published: true });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title) return toast.error("Add a title");
    setSaving(true);
    try {
      if (isEdit) { await api.patch(`/resources/${resource.id}`, form); toast.success("Resource updated"); }
      else { await api.post("/resources", form); toast.success("Resource added"); }
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{isEdit ? "Edit resource" : "Add resource"}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">{isEdit ? "Edit this library resource." : "Add a new resource to the library."}</DialogDescription>
        <div className="space-y-3.5">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="resource-title-input" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Type</Label><Select value={form.resource_type} onValueChange={(v) => setForm({ ...form, resource_type: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{Object.keys(TYPE_ICON).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label>URL / link</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="mt-1" placeholder="https://" /></div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="resource-featured" /> Featured</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="resource-published" /> Published</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-resource-btn">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : (isEdit ? "Save" : "Add")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
